import React, { useEffect, useCallback, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  useColorModeValue,
  Icon,
  ModalHeader,
  ModalBody,
  Text,
  Center,
  Stack,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { generateSecurityToken } from "state/subscription/operations";
import { PurchasingCartConfirm } from "./PurchasingCartConfirm";
import type { DealFinderTicker } from "../../../../api/tickers/models/DealFinderTicker";
import type { ProductOptionStatus, PurchaseStatus } from "api/billingPlan/models/PurchaseStatus";
import { getPurchaseStatus } from "../../../../api/billingPlan";
import { RiLock2Fill } from "react-icons/ri";
import { track } from "api/analytics";
import { DEAL_FINDER_PAYMENT_CLOSED } from "api/analytics/events";
import { useUserProfile } from "hooks";
import type { AvailableForPurchase } from "api/billingPlan/models/AvailableForPurchase";
import type { ComponentInfo } from "api/billingPlan/models/ComponentInfo";

interface PurchasingCartModalProps {
  isOpen: boolean;
  onClose: (success?: boolean) => void;
  deal: DealFinderTicker | null;
  selectedProductOption?: ProductOptionStatus | undefined;
  selectedProductKey?: string;
}

export const PurchasingCartModal: React.FC<PurchasingCartModalProps> = ({
  isOpen,
  onClose,
  deal,
  selectedProductOption,
  selectedProductKey,
}) => {
  const dispatch = useDispatch();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const { fullName, email, id: userId } = useUserProfile();
  const [purchaseStatus, setPurchaseStatus] = useState<PurchaseStatus | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(selectedProductKey);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(generateSecurityToken());
  }, [dispatch]);

  // Fetch purchase status when modal opens if selectedProductOption not provided
  useEffect(() => {
    const fetchPurchaseStatus = async () => {
      if (!isOpen || !userId || !deal?.id) return;
      if (selectedProductOption && selectedProductKey) {
        setPurchaseStatus({
          [selectedProductKey]: selectedProductOption,
        } as PurchaseStatus);
        return;
      }

      setIsLoading(true);
      try {
        const status = await getPurchaseStatus(userId, deal.id);
        setPurchaseStatus(status);
        const firstAvailableOption = Object.keys(status).find((key) => status[key].status !== "purchased") || Object.keys(status)[0];

        if (firstAvailableOption) {
          setSelectedKey(firstAvailableOption);
        }
      } catch (error) {
        console.error("Error fetching purchase status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchaseStatus();
  }, [isOpen, userId, deal, selectedProductOption, selectedProductKey]);

  const handleOnClose = useCallback(() => {
    track(DEAL_FINDER_PAYMENT_CLOSED, {
      dealName: deal?.name,
      dealExchange: deal?.exchange,
      dealTicker: deal?.symbol,
      userName: fullName,
      userEmail: email,
    });
    onClose();
  }, [deal, email, fullName, onClose]);

  if (!deal) return null;

  // Show loading state while fetching purchase status
  if (isLoading || !purchaseStatus || !selectedKey) {
    return (
      <Modal isOpen={isOpen} onClose={handleOnClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent
          bg={bgColor}
          maxW={{ base: "100%", lg: "1200px" }}
          maxH={{ base: "100%", lg: "90vh" }}
          height={"100%"}
          mt={{ base: 0, lg: "auto" }}
          mb={{ base: 0, lg: "auto" }}
          borderRadius={{ base: 0, lg: "xl" }}>
          <ModalHeader p={2} pl="2rem" borderBottom="1px" borderColor={borderColor} position="relative">
            <Center height="3rem">
              <Stack direction="row" spacing="1rem">
                <Icon as={RiLock2Fill} boxSize="1.5rem" />
                <Text fontWeight="semibold" fontSize="xl">
                  Loading...
                </Text>
              </Stack>
            </Center>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody p={4} display="flex" alignItems="center" justifyContent="center">
            <Text>Loading Secure Checkout...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  const productOption = purchaseStatus[selectedKey];
  if (!productOption) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleOnClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        className="ch-investment-purchase-modal"
        bg={bgColor}
        maxW={{ base: "100%", lg: "1200px" }}
        maxH={{ base: "100%", lg: "90vh" }}
        height={"100%"}
        mt={{ base: 0, lg: "auto" }}
        mb={{ base: 0, lg: "auto" }}
        borderRadius={{ base: 0, lg: "xl" }}>
        <ModalHeader p={2} pl="2rem" borderBottom="1px" borderColor={borderColor} position="relative">
          <Center height="3rem">
            <Stack direction="row" spacing="1rem">
              <Icon as={RiLock2Fill} boxSize="1.5rem" />
              <Text fontWeight="semibold" fontSize="xl">
                Secure Checkout
              </Text>
            </Stack>
          </Center>
          <ModalCloseButton />
        </ModalHeader>
        <ModalCloseButton color="gray.300" _hover={{}} />
        <ModalBody p={0} maxH={{ base: "100%", lg: "95vh" }} overflow={{ base: "auto", lg: "hidden" }}>
          <PurchasingCartConfirm
            deal={deal}
            selectedProductKey={selectedKey}
            selectedProductOption={productOption as ComponentInfo & AvailableForPurchase}
            onClose={onClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
